import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock3, Play } from 'lucide-react';

// Convierte links de YouTube (watch, youtu.be, shorts) a URL embebible.
// Devuelve null si no es YouTube (en ese caso se asume archivo de video directo).
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/,
  );
  if (!match) return null;
  return `https://www.youtube-nocookie.com/embed/${match[1]}?autoplay=1&rel=0`;
};

/**
 * Card de video demo para el sitio público.
 * - Con `video.videoUrl`: muestra el poster y, al hacer clic, reproduce embebido
 *   (YouTube o <video> nativo) sin cargar iframes hasta que el visitante lo pida.
 * - Sin `videoUrl`: muestra el poster con estado "próximamente" y deriva al trial,
 *   para que la card venda igual aunque el video todavía no esté grabado.
 */
export default function DemoVideoCard({ video, poster, fallbackHref = '/registro-onboarding', compact = false }) {
  const [playing, setPlaying] = useState(false);
  const hasVideo = Boolean(video.videoUrl);
  const youTubeEmbed = getYouTubeEmbedUrl(video.videoUrl);

  return (
    <article
      id={`video-${video.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-[2rem] border border-[var(--gst-border-strong)] bg-[var(--surface)] shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
    >
      <div className="relative aspect-video overflow-hidden bg-[var(--gst-graphite-900)]">
        {playing && hasVideo ? (
          youTubeEmbed ? (
            <iframe
              src={youTubeEmbed}
              title={video.title}
              className="absolute inset-0 h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video src={video.videoUrl} className="absolute inset-0 h-full w-full object-cover" controls autoPlay />
          )
        ) : (
          <>
            <img
              src={poster}
              alt={video.title}
              className={`h-full w-full object-cover transition duration-500 group-hover:scale-105 ${hasVideo ? '' : 'opacity-80'}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(31,36,33,0.72)] via-[rgba(31,36,33,0.12)] to-transparent" />

            <div className="absolute left-4 top-4 flex items-center gap-2">
              <span className="rounded-full bg-[var(--surface)]/92 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-[var(--gst-graphite-900)]">
                {video.circuit}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--gst-graphite-900)]/80 px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-white">
                <Clock3 size={11} />
                {video.duration}
              </span>
            </div>

            {hasVideo ? (
              <button
                type="button"
                onClick={() => setPlaying(true)}
                aria-label={`Reproducir: ${video.title}`}
                className="absolute inset-0 flex items-center justify-center"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--gst-mint-500)] text-[var(--gst-graphite-900)] shadow-[0_14px_36px_rgba(16,185,129,0.45)] transition group-hover:scale-110">
                  <Play size={26} fill="currentColor" className="ml-1" />
                </span>
              </button>
            ) : (
              <div className="absolute inset-x-4 bottom-4">
                <span className="inline-flex rounded-full border border-white/25 bg-[var(--gst-graphite-900)]/75 px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-[var(--gst-mint-200)]">
                  Video en camino
                </span>
              </div>
            )}
          </>
        )}
      </div>

      <div className={compact ? 'flex flex-1 flex-col p-5' : 'flex flex-1 flex-col p-6'}>
        <h3 className="text-xl font-black leading-snug text-[var(--gst-graphite-900)]">{video.title}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-[var(--gst-graphite-700)]">{video.description}</p>
        {!hasVideo && (
          <Link
            to={fallbackHref}
            className="mt-4 inline-flex items-center gap-2 text-sm font-black uppercase tracking-[0.12em] text-[var(--gst-mint-700)] transition hover:gap-3"
          >
            Probalo vos en tu cuenta gratis <ArrowRight size={15} />
          </Link>
        )}
      </div>
    </article>
  );
}
